import React from 'react';
import { Home, Pill, HelpCircle, Mic, Clock, Check, User, AlertCircle, ArrowLeft, Phone, Search, CheckCircle, X, MessageSquare, Video, CalendarDays, AlertTriangle } from 'lucide-react';
import { convertDosesToProgressItems, formatTimeToReadable, getDoseStatus, getNextDueDose } from '../utils/doseTimeUtils';
import { useThemeStyles, getMedicationColor, buttonStyles } from '../utils/themeUtils';

/**
 * Primary App Prototype
 * Voice-first smart display interface
 * Props: screen, userName, medications, medicationsTaken, currentMedIndex, 
 *        isListening, voiceStatus, transcript, onNavigate, onVoiceButton, 
 *        onMedicationTaken, startListening, stopListening, theme
 */

// Reusable Header Component
export const PrimaryHeader = ({ userName, theme = 'light', debugTimeOverride = false, debugTime = null }) => {
  const [currentTime, setCurrentTime] = React.useState(() => {
    return debugTimeOverride && debugTime ? debugTime : new Date();
  });

  // Update time every second (skip if debug override is enabled)
  React.useEffect(() => {
    if (debugTimeOverride) {
      // If debug override is enabled, use debug time and don't update
      setCurrentTime(debugTime);
      return;
    }
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [debugTimeOverride, debugTime]);

  // Update when debugTimeOverride changes
  React.useEffect(() => {
    if (debugTimeOverride && debugTime) {
      setCurrentTime(debugTime);
    }
  }, [debugTimeOverride, debugTime]);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
  };

  // Format time with AM/PM
  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const { cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);

  return (
    <div className={`${cardBg} px-12 py-6 flex items-center justify-between ${borderColor}`}>
      <div className="flex items-center">
        <div className={`w-16 h-16 ${'bg-gradient-to-br from-indigo-500 to-purple-500'} rounded-full flex items-center justify-center mr-6`}>
          <User className={`w-8 h-8 ${'text-white'}`} />
        </div>
        <div>
          <h1 className={`text-4xl font-bold ${textColor}`}>{getGreeting()}, {userName}</h1>
          <div className="flex items-center gap-3">
            <p className={`text-2xl ${textSecondary}`}>{currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            <p className={`text-2xl font-semibold ${textColor}`}>{formatTime()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Voice Command Popup Component
const VoiceCommandPopup = ({ isOpen, isListening, onClose, theme = 'light', startListening = null, stopListening = null }) => {
  const { cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);
  
  const voiceCommands = [
    {icon: Check, text: '"I took my medication"'},
    {icon: Clock, text: '"Remind me later"'},
    {icon: AlertCircle, text: '"I cannot find my pills"'},
    {icon: Pill, text: '"Show medications"'},
    {icon: Phone, text: '"Call Sarah"'}
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div 
        className={`${cardBg} rounded-3xl p-8 max-w-md w-full mx-4 ${borderColor} fixed bottom-28 right-12 animate-in slide-in-from-bottom-4 fade-in duration-200`}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: 'translateX(0)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${isListening ? 'bg-gradient-to-r from-red-500 to-pink-500 animate-pulse' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}>
              <Mic className="w-6 h-6 text-white"/>
            </div>
            <div>
              <h3 className={`text-2xl font-bold ${textColor}`}>
                {isListening ? 'Listening...' : 'Voice Commands'}
              </h3>
              {isListening && (
                <p className={`text-sm ${textSecondary} mt-1`}>Speak your command</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isListening && startListening && (
              <button
                onClick={() => {
                  startListening();
                }}
                className={`px-4 py-2 rounded-xl ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white font-semibold transition-all`}
              >
                Start
              </button>
            )}
            {isListening && stopListening && (
              <button
                onClick={() => {
                  stopListening();
                  onClose();
                }}
                className={`px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-all`}
              >
                Stop
              </button>
            )}
            <button
              onClick={onClose}
              className={`p-2 rounded-xl ${theme === 'dark' ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} transition-all`}
              aria-label="Close"
            >
              <X className={`w-5 h-5 ${textSecondary}`} />
            </button>
          </div>
        </div>
        
        {!isListening && (
          <div className="space-y-3">
            <p className={`text-lg font-semibold ${textColor} mb-3`}>Try saying:</p>
            {voiceCommands.map((cmd, index) => {
              const CommandIcon = cmd.icon;
              return (
                <div key={index} className={`flex items-center p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700 bg-opacity-60' : 'bg-blue-50'}`}>
                  <CommandIcon className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mr-4 flex-shrink-0`} />
                  <span className={`text-lg ${textSecondary}`}>{cmd.text}</span>
                </div>
              );
            })}
          </div>
        )}
        
        {isListening && (
          <div className="text-center py-8">
            <div className="relative w-40 h-40 mx-auto mb-4 flex items-center justify-center">
              {/* Pulsing circles animation - Alexa/Siri style */}
              <div className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-red-500 to-pink-500 opacity-20 animate-ping" style={{ animationDuration: '2s' }}></div>
              <div className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-red-500 to-pink-500 opacity-20 animate-ping" style={{ animationDelay: '0.3s', animationDuration: '2s' }}></div>
              <div className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-red-500 to-pink-500 opacity-20 animate-ping" style={{ animationDelay: '0.6s', animationDuration: '2s' }}></div>
              {/* Center microphone */}
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center z-10">
                <Mic className="w-14 h-14 text-white animate-pulse"/>
              </div>
            </div>
            <p className={`text-xl ${textColor} font-semibold animate-pulse`}>Listening...</p>
            <p className={`text-base ${textSecondary} mt-2`}>Speak your command now</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Bottom Navigation Component (merged with header)
export const PrimaryBottomNav = ({ onNavigate, theme = 'light', currentScreen = 'home', isListening = false, startListening = null, stopListening = null, userName = '', debugTimeOverride = false, debugTime = null }) => {
  const { cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);
  const [showVoicePopup, setShowVoicePopup] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(() => {
    return debugTimeOverride && debugTime ? debugTime : new Date();
  });

  // Update time every second (skip if debug override is enabled)
  React.useEffect(() => {
    if (debugTimeOverride) {
      // If debug override is enabled, use debug time and don't update
      setCurrentTime(debugTime);
      return;
    }
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [debugTimeOverride, debugTime]);

  // Update when debugTimeOverride changes
  React.useEffect(() => {
    if (debugTimeOverride && debugTime) {
      setCurrentTime(debugTime);
    }
  }, [debugTimeOverride, debugTime]);

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
  };

  // Format time with AM/PM
  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Close popup when listening stops
  React.useEffect(() => {
    if (!isListening && showVoicePopup) {
      const timer = setTimeout(() => {
        setShowVoicePopup(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isListening, showVoicePopup]);

  const handleVoiceClick = () => {
    setShowVoicePopup(true);
  };

  const handleClosePopup = () => {
    setShowVoicePopup(false);
    if (isListening && stopListening) {
      stopListening();
    }
  };

  return (
    <>
      <VoiceCommandPopup
        isOpen={showVoicePopup}
        isListening={isListening}
        onClose={handleClosePopup}
        theme={theme}
        startListening={startListening}
        stopListening={stopListening}
      />
      <div className={`${cardBg} border-t ${borderColor} px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 fixed bottom-0 left-0 right-0 z-50`}>
        <div className="flex items-center justify-between gap-4 sm:gap-6 md:gap-8">
          {/* Left side: Greeting */}
          <div className="flex-shrink-0">
            <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold ${textColor}`}>{getGreeting()}, {userName}</h1>
          </div>

          {/* Center: Time - Large */}
          <div className="flex-1 flex justify-center">
            <p className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold ${textColor}`}>{formatTime()}</p>
          </div>

          {/* Right side: Navigation Icons - Evenly Spaced */}
          <div className="flex items-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 flex-shrink-0">
            <button 
              onClick={() => onNavigate('medication-list')} 
              className={`flex flex-col items-center min-w-[50px] sm:min-w-[60px] ${currentScreen === 'medication-list' ? (theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600') : textSecondary}`}
              aria-label="Medications"
            >
              <Pill className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2"/>
              <span className="text-sm sm:text-base md:text-lg font-semibold">MEDS</span>
              {currentScreen === 'medication-list' && (
                <div className={`w-2 h-2 ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-600'} rounded-full mt-1`}></div>
              )}
            </button>
            <button 
              onClick={() => onNavigate('home')} 
              className={`flex flex-col items-center min-w-[50px] sm:min-w-[60px] ${currentScreen === 'home' ? (theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600') : textSecondary}`}
              aria-label="Home"
            >
              <Home className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2"/>
              <span className="text-sm sm:text-base md:text-lg font-semibold">HOME</span>
              {currentScreen === 'home' && (
                <div className={`w-2 h-2 ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-600'} rounded-full mt-1`}></div>
              )}
            </button>
            <button 
              onClick={() => onNavigate('help')} 
              className={`flex flex-col items-center min-w-[50px] sm:min-w-[60px] ${currentScreen === 'help' ? (theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600') : textSecondary}`}
              aria-label="Contacts"
            >
              <User className="w-8 h-8 sm:w-10 sm:h-10 mb-1 sm:mb-2"/>
              <span className="text-sm sm:text-base md:text-lg font-semibold">CONTACTS</span>
              {currentScreen === 'help' && (
                <div className={`w-2 h-2 ${theme === 'dark' ? 'bg-indigo-400' : 'bg-indigo-600'} rounded-full mt-1`}></div>
              )}
            </button>
            <button 
              onClick={handleVoiceClick} 
              className={`flex flex-col items-center min-w-[50px] sm:min-w-[60px] ${isListening ? 'text-red-500' : textSecondary}`}
              aria-label="Voice commands"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-1 sm:mb-2 transition-all ${isListening ? 'bg-gradient-to-r from-red-500 to-pink-500 animate-pulse' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}>
                <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-white"/>
              </div>
              <span className="text-sm sm:text-base md:text-lg font-semibold">VOICE</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export const PrimaryAmbient = ({ onNavigate, theme = 'light', debugTimeOverride = false, debugTime = null, medications = [], medicationLogs = [] }) => {
  const bgClass = theme === 'dark'
    ? 'bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900'
    : 'bg-gradient-to-br from-slate-50 to-blue-50';
  
  const textColor = theme === 'dark' ? 'text-white' : 'text-slate-900';
  const textSecondary = theme === 'dark' ? 'text-indigo-200' : 'text-indigo-600';
  const cardBg = theme === 'dark'
    ? 'bg-white bg-opacity-15 backdrop-blur-lg border-white border-opacity-30 border-2'
    : 'bg-white bg-opacity-90 border-2 border-slate-200';
  
  const currentTime = debugTimeOverride && debugTime ? debugTime : new Date();
  
  // Find the next upcoming medication dose
  const nextMedication = React.useMemo(() => {
    let nextMed = null;
    let nextDoseTime = null;
    let earliestTime = null;
    
    // First, try to find upcoming doses
    for (const med of medications) {
      if (!med.doses || med.doses.length === 0) continue;
      
      const doseStatuses = getDoseStatus(med, medicationLogs, currentTime);
      
      // Find the next upcoming dose (not taken, isUpcoming)
      const nextDose = doseStatuses.find(status => 
        !status.isTaken && status.isUpcoming
      );
      
      if (nextDose) {
        // Parse the scheduled time to compare
        const [hours, minutes] = nextDose.scheduled_time.split(':').map(Number);
        const doseDateTime = new Date(currentTime);
        doseDateTime.setHours(hours, minutes, 0, 0);
        
        // Check if this is earlier than our current earliest
        if (!earliestTime || doseDateTime < earliestTime) {
          earliestTime = doseDateTime;
          nextMed = med;
          nextDoseTime = nextDose.scheduled_time;
        }
      }
    }
    
    // If no upcoming doses, look for due or late doses that can be taken
    if (!nextMed) {
      for (const med of medications) {
        if (!med.doses || med.doses.length === 0) continue;
        
        const doseStatuses = getDoseStatus(med, medicationLogs, currentTime);
        
        // Find due or late doses that can be taken
        const nextDose = doseStatuses.find(status => 
          !status.isTaken && (status.isDue || (status.isLate && status.canTakeNow))
        );
        
        if (nextDose) {
          // Parse the scheduled time to compare
          const [hours, minutes] = nextDose.scheduled_time.split(':').map(Number);
          const doseDateTime = new Date(currentTime);
          doseDateTime.setHours(hours, minutes, 0, 0);
          
          // Check if this is earlier than our current earliest
          if (!earliestTime || doseDateTime < earliestTime) {
            earliestTime = doseDateTime;
            nextMed = med;
            nextDoseTime = nextDose.scheduled_time;
          }
        }
      }
    }
    
    return nextMed ? { medication: nextMed, scheduledTime: nextDoseTime } : null;
  }, [medications, medicationLogs, currentTime]);
  
  return (
    <div className={`min-h-screen ${bgClass} flex items-center justify-center p-12 cursor-pointer relative`} onClick={() => onNavigate('home')}>
      <div className="text-center relative z-10">
        <div className={`text-9xl font-light ${textColor} mb-4`}>
          {currentTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
        </div>
        <div className={`text-4xl ${textSecondary}`}>
          {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
        {nextMedication ? (
          <div className={`mt-12 ${cardBg} rounded-3xl p-8`}>
            <div className="flex items-center justify-center">
              <Clock className={`w-12 h-12 ${textSecondary} mr-4`} />
              <div className="text-left">
                <p className={`text-2xl ${textSecondary}`}>Next Medication</p>
                <p className={`text-4xl font-bold ${textColor}`}>{nextMedication.medication.name}</p>
                <p className={`text-2xl ${textSecondary} mt-2`}>{formatTimeToReadable(nextMedication.scheduledTime)}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className={`mt-12 ${cardBg} rounded-3xl p-8`}>
            <div className="flex items-center justify-center">
              <Clock className={`w-12 h-12 ${textSecondary} mr-4`} />
              <div className="text-left">
                <p className={`text-2xl ${textSecondary}`}>Next Medication</p>
                <p className={`text-4xl font-bold ${textColor}`}>None Scheduled</p>
              </div>
            </div>
          </div>
        )}
        <div className="mt-12 animate-pulse">
          <div className={`w-32 h-32 ${theme === 'dark' ? 'bg-white bg-opacity-30 backdrop-blur-sm border-white border-opacity-40 border-2' : 'bg-indigo-500'} rounded-full mx-auto mb-6 flex items-center justify-center`}>
            <Mic className="w-16 h-16 text-white" />
          </div>
          <p className={`text-3xl ${textColor} font-medium`}>Say "Hey Assistant" or tap anywhere</p>
        </div>
      </div>
    </div>
  );
};

export const PrimaryHome = ({ userName, medications = [], medicationsLoading = false, currentMedIndex, isListening, voiceStatus, transcript, onNavigate, startListening, stopListening, theme = 'light', medicationLogs = [], onMedicationTaken = null, debugTimeOverride = false, debugTime = null }) => {
  // Get current time (debug or real)
  const currentTime = debugTimeOverride && debugTime ? debugTime : new Date();
  
  // Find ALL medications that are due now or overdue but can still be taken
  const dueNowMedications = React.useMemo(() => {
    const dueMeds = [];
    
    for (const med of medications) {
      if (!med.doses || med.doses.length === 0) continue;
      
      const doseStatuses = getDoseStatus(med, medicationLogs, currentTime);
      
      // Skip if all doses are taken
      const allDosesTaken = doseStatuses.every(status => status.isTaken);
      if (allDosesTaken) continue;
      
      // Find a dose that is due now (isDue) or overdue but can still be taken (isLate && canTakeNow)
      const dueDose = doseStatuses.find(status => 
        !status.isTaken && (status.isDue || (status.isLate && status.canTakeNow))
      );
      
      if (dueDose) {
        dueMeds.push({ med, doseStatus: dueDose });
      }
    }
    
    return dueMeds;
  }, [medications, medicationLogs, currentTime]);

  // Track which medication is currently in view (for action buttons)
  const [currentMedIndexInCarousel, setCurrentMedIndexInCarousel] = React.useState(0);
  const carouselRef = React.useRef(null);

  // Get current medication from carousel
  const currentMed = dueNowMedications[currentMedIndexInCarousel]?.med || null;
  const currentDueDose = dueNowMedications[currentMedIndexInCarousel]?.doseStatus?.dose_number || null;
  const doseTime = currentMed && currentDueDose && currentMed.doses
    ? currentMed.doses.find(d => d.dose_number === currentDueDose)?.scheduled_time
    : null;

  // Get guidance message for current dose
  const doseStatuses = currentMed ? getDoseStatus(currentMed, medicationLogs, currentTime) : [];
  const currentDoseStatus = doseStatuses.find(status => status.dose_number === currentDueDose);
  const guidanceMessage = currentDoseStatus?.guidanceMessage || '';
  const canTakeNow = currentDoseStatus?.canTakeNow || false;

  // Handle scroll to update current medication index using IntersectionObserver
  React.useEffect(() => {
    if (!carouselRef.current || dueNowMedications.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0', 10);
            setCurrentMedIndexInCarousel(index);
          }
        });
      },
      {
        root: carouselRef.current,
        threshold: 0.5,
      }
    );

    const items = carouselRef.current.querySelectorAll('.carousel-item');
    items.forEach((item) => observer.observe(item));

    return () => {
      items.forEach((item) => observer.unobserve(item));
    };
  }, [dueNowMedications.length]);

  // Fallback scroll handler
  const handleScroll = React.useCallback(() => {
    if (!carouselRef.current) return;
    
    const container = carouselRef.current;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.querySelector('.carousel-item')?.offsetWidth || 0;
    const gap = 16; // gap-4 = 16px
    const newIndex = Math.round(scrollLeft / (itemWidth + gap));
    
    if (newIndex !== currentMedIndexInCarousel && newIndex >= 0 && newIndex < dueNowMedications.length) {
      setCurrentMedIndexInCarousel(newIndex);
    }
  }, [currentMedIndexInCarousel, dueNowMedications.length]);
  

  // Separate medications into completed, missed, and upcoming based on dose-specific tracking
  // Each medication appears in ONLY ONE category (priority: missed > taken > upcoming)
  const categorizedMedications = React.useMemo(() => {
    const taken = [];
    const missed = [];
    const upcoming = [];

    medications.forEach(med => {
      if (!med.doses || med.doses.length === 0) return;

      const doseStatuses = getDoseStatus(med, medicationLogs, currentTime);
      const takenDoses = doseStatuses.filter(status => status.isTaken);
      const missedDoses = doseStatuses.filter(status => status.isMissed);
      const upcomingDoses = doseStatuses.filter(status => status.isUpcoming);

      // Medications can appear in multiple sections if they have mixed statuses
      // Add to each relevant section
      if (missedDoses.length > 0) {
        // Has missed doses - add to missed section
        missed.push({ med, doseStatuses: missedDoses });
      }
      if (upcomingDoses.length > 0) {
        // Has upcoming doses - add to upcoming section
        upcoming.push({ med, doseStatuses: upcomingDoses });
      }
      if (takenDoses.length > 0) {
        // Has taken doses - add to taken section
        taken.push({ med, doseStatuses: takenDoses });
      }
    });

    // Sort by earliest dose time within each category
    const sortByTime = (a, b) => {
      const aTime = a.doseStatuses[0]?.scheduled_time;
      const bTime = b.doseStatuses[0]?.scheduled_time;
      if (!aTime || !bTime) return 0;
      return aTime.localeCompare(bTime);
    };

    return {
      taken: taken.sort(sortByTime),
      missed: missed.sort(sortByTime),
      upcoming: upcoming.sort(sortByTime) // Upcoming will be displayed at top
    };
  }, [medications, medicationLogs, currentTime]);

  const { taken: takenMedications, missed: missedMedications, upcoming: upcomingMedications } = categorizedMedications;

  const { bgClass, cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);

  return (
    <div className={`h-screen ${bgClass} flex flex-col relative overflow-hidden`}>
      {transcript && (
        <div className={`${theme === 'dark' ? 'bg-indigo-900 bg-opacity-50' : 'bg-indigo-50'} border-b-2 border-indigo-300 px-4 sm:px-6 md:px-12 py-2 sm:py-3 md:py-4 flex-shrink-0`}>
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl ${textColor}`}><span className="font-semibold">You said:</span> "{transcript}"</p>
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 p-4 sm:p-6 md:p-8 lg:p-12 relative z-10 min-h-0 overflow-hidden" style={{ paddingBottom: '110px', boxSizing: 'border-box', height: 'calc(100% - 110px)' }}>
        <div className="flex flex-col min-h-0" style={{ maxHeight: '100%' }}>
          <div className={`${cardBg} rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 flex flex-col overflow-hidden ${borderColor}`} style={{ height: '100%', maxHeight: '100%' }}>
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
              {/* Due Now Title */}
              <div className="flex items-center mb-4 sm:mb-6 md:mb-8 flex-shrink-0">
                <Clock className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} mr-2 sm:mr-3 md:mr-4`} />
                <h2 className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold ${textColor}`}>Due Now</h2>
              </div>
              
              {medicationsLoading ? (
                <div className="text-center py-8">
                  <p className={`text-base sm:text-lg md:text-xl ${textColor}`}>Loading medications...</p>
                </div>
              ) : dueNowMedications.length > 0 ? (
                <>
                  {/* Carousel Container */}
                  <div className="mb-4 sm:mb-6 md:mb-8 flex-shrink-0">
                    <div
                      ref={carouselRef}
                      onScroll={handleScroll}
                      className="flex gap-4 overflow-x-scroll scrollbar-hide"
                      style={{
                        scrollSnapType: 'x mandatory',
                        scrollbarWidth: 'none', // Firefox
                        msOverflowStyle: 'none', // IE and Edge
                      }}
                    >
                      {dueNowMedications.map(({ med, doseStatus }, index) => {
                        const medDoseTime = med.doses?.find(d => d.dose_number === doseStatus.dose_number)?.scheduled_time;
                        const medDoseStatuses = getDoseStatus(med, medicationLogs, currentTime);
                        const medDoseStatus = medDoseStatuses.find(s => s.dose_number === doseStatus.dose_number);
                        const medGuidanceMessage = medDoseStatus?.guidanceMessage || '';
                        const medCanTakeNow = medDoseStatus?.canTakeNow || false;
                        const medColor = getMedicationColor(med.id, medications);
                        
                        // Map gradient colors to solid background colors
                        const colorMap = {
                          'blue': theme === 'dark' ? 'bg-blue-800' : 'bg-blue-200',
                          'purple': theme === 'dark' ? 'bg-purple-800' : 'bg-purple-200',
                          'pink': theme === 'dark' ? 'bg-pink-800' : 'bg-pink-200',
                          'indigo': theme === 'dark' ? 'bg-indigo-800' : 'bg-indigo-200',
                          'cyan': theme === 'dark' ? 'bg-cyan-800' : 'bg-cyan-200',
                          'teal': theme === 'dark' ? 'bg-teal-800' : 'bg-teal-200',
                          'emerald': theme === 'dark' ? 'bg-emerald-800' : 'bg-emerald-200',
                          'lime': theme === 'dark' ? 'bg-lime-800' : 'bg-lime-200',
                          'amber': theme === 'dark' ? 'bg-amber-800' : 'bg-amber-200',
                          'orange': theme === 'dark' ? 'bg-orange-800' : 'bg-orange-200',
                          'red': theme === 'dark' ? 'bg-red-800' : 'bg-red-200',
                          'rose': theme === 'dark' ? 'bg-rose-800' : 'bg-rose-200',
                          'violet': theme === 'dark' ? 'bg-violet-800' : 'bg-violet-200',
                          'fuchsia': theme === 'dark' ? 'bg-fuchsia-800' : 'bg-fuchsia-200',
                          'sky': theme === 'dark' ? 'bg-sky-800' : 'bg-sky-200',
                        };
                        
                        // Extract color name from gradient class
                        let bgColorClass = theme === 'dark' ? 'bg-slate-800' : 'bg-slate-200';
                        if (medColor) {
                          const gradientClass = theme === 'dark' ? medColor.dark : medColor.light;
                          const colorMatch = gradientClass.match(/from-(\w+)-/);
                          if (colorMatch && colorMap[colorMatch[1]]) {
                            bgColorClass = colorMap[colorMatch[1]];
                          }
                        }

                        return (
                          <div
                            key={`${med.id}-${doseStatus.dose_number}`}
                            data-index={index}
                            className="carousel-item flex-shrink-0 w-[75%] sm:w-[70%] md:w-[65%] lg:w-[60%] xl:w-[55%]"
                            style={{ scrollSnapAlign: 'center' }}
                          >
                            <div className={`${bgColorClass} rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 h-full flex flex-col overflow-hidden ${borderColor} border-2`}>
                              {/* Top: Medication Name and Purpose */}
                              <div className="mb-4 sm:mb-6 flex-shrink-0">
                                <div className="flex items-center gap-2 sm:gap-3 min-w-0" style={{ fontSize: 'clamp(0.75rem, 2vw + 0.5rem, 2.5rem)' }}>
                                  <p className={`font-bold ${textColor} flex-shrink min-w-0`} style={{ fontSize: 'inherit', lineHeight: '1.2' }}>{med.name}</p>
                                  {med.purpose && (
                                    <span className={`inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-full text-xs sm:text-sm md:text-base lg:text-lg font-semibold flex-shrink-0 whitespace-nowrap ${theme === 'dark' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-800'}`}>
                                      {med.purpose}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Two Columns: Left (Directions + Guidance), Right (Pill + Description) - Stacks on smaller screens */}
                              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 flex-1 min-h-0 overflow-hidden">
                                {/* Left Side: Directions and Guidance */}
                                <div className="flex flex-col gap-4 sm:gap-6 min-h-0 overflow-hidden order-1">
                                  {/* Directions */}
                                  <div className="flex-shrink-0">
                                    <p className={`text-base sm:text-lg md:text-xl font-semibold ${textColor}`}>{med.instructions || `Take ${med.dosage}`}</p>
                                    {doseStatus.dose_number && med.doses && med.doses.length > 1 && (
                                      <p className={`text-xs sm:text-sm md:text-base ${textSecondary} mt-1 sm:mt-2`}>
                                        Dose {doseStatus.dose_number} of {med.doses.length}
                                        {medDoseTime && ` • ${formatTimeToReadable(medDoseTime)}`}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {/* Guidance Message - No background rectangle */}
                                  {medGuidanceMessage && (
                                    <div className="flex-1 min-h-0 overflow-y-auto">
                                      <p className={`text-sm sm:text-base md:text-lg font-semibold ${textColor} mb-1 sm:mb-2`}>Guidance:</p>
                                      <p className={`text-sm sm:text-base md:text-lg ${textSecondary}`}>{medGuidanceMessage}</p>
                                    </div>
                                  )}
                                </div>

                                {/* Right Side: Pill Picture and Description - Stacks below guidance on smaller screens */}
                                <div className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 min-h-0 order-2">
                                  {/* Placeholder Pill Image - Responsive sizing with max constraints */}
                                  <div className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 xl:w-36 xl:h-36 max-w-full max-h-full rounded-xl sm:rounded-2xl md:rounded-3xl flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'} border-2 overflow-hidden`}>
                                    <Pill className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 xl:w-20 xl:h-20 max-w-[90%] max-h-[90%] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`} />
                                  </div>
                                  
                                  {/* Pill Description */}
                                  <div className="flex-1 min-h-0 overflow-y-auto">
                                    <p className={`text-sm sm:text-base md:text-lg ${textSecondary} text-center lg:text-left`}>{med.form}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons - Only show when medication can be taken */}
                              {medCanTakeNow && (
                                <div className="mt-4 sm:mt-6 flex-shrink-0">
                                  <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
                                    {onMedicationTaken && (
                                      <button
                                        onClick={() => onMedicationTaken(med.id, doseStatus.dose_number)}
                                        className={`${buttonStyles.primary} ${buttonStyles.small} ${buttonStyles.interactive}`}
                                        aria-label={`Mark ${med.name} dose ${doseStatus.dose_number} as taken`}
                                      >
                                        <Check className={`w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mb-1 sm:mb-2 ${'text-white'}`}/>
                                        <span>I Took It</span>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => onNavigate('home')}
                                      className={`${buttonStyles.secondary} ${buttonStyles.small} ${buttonStyles.interactive}`}
                                      aria-label="Remind me later about this medication"
                                    >
                                      <Clock className={`w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 mb-1 sm:mb-2 ${'text-white'}`}/>
                                      <span>Remind Me Later</span>
                                    </button>
                                  </div>
                                  {/* Need Help Button - Small, centered, no rectangle */}
                                  <div className="flex justify-center">
                                    <button
                                      onClick={() => onNavigate('cant-take')}
                                      className={`flex items-center gap-2 ${textColor} font-bold text-sm sm:text-base md:text-lg hover:opacity-80 transition-opacity`}
                                      aria-label="Get help with this medication"
                                    >
                                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                                      <span>Need Help</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Hide scrollbar */}
                    <style>{`
                      .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                      }
                    `}</style>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className={`text-base sm:text-lg md:text-xl ${textColor}`}>No meds due</p>
                </div>
              )}
              </div>
            </div>
          </div>

        <div className="flex flex-col min-h-0 overflow-hidden" style={{ maxHeight: '100%' }}>
          <div className={`${cardBg} rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 ${borderColor} overflow-y-auto`} style={{ height: '100%', maxHeight: '100%' }}>
            <div className="flex items-center mb-4 sm:mb-6 md:mb-8 flex-shrink-0">
              <CalendarDays className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'} mr-2 sm:mr-3 md:mr-4`} />
              <h2 className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold ${textColor}`}>
                {(debugTimeOverride && debugTime ? debugTime : new Date()).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </h2>
            </div>
            
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Upcoming Medications Section - Show at top */}
              <div className="mb-4 sm:mb-6 md:mb-8">
                <h3 className={`text-base sm:text-lg md:text-xl lg:text-2xl font-semibold ${textColor} mb-2 sm:mb-3 md:mb-4 flex items-center`}>
                  <Clock className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} mr-1 sm:mr-2`} />
                  Upcoming ({upcomingMedications.length})
                </h3>
                {upcomingMedications.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                    {upcomingMedications.map(({ med, doseStatuses }) => {
                      const upcomingDoses = doseStatuses.filter(s => s.isUpcoming);
                      const medColor = getMedicationColor(med.id, medications);
                      const upcomingBg = medColor
                        ? (theme === 'dark' ? medColor.dark : medColor.light)
                        : (theme === 'dark' ? 'bg-blue-600 bg-opacity-80' : 'bg-gradient-to-r from-blue-50 to-cyan-50');
                      const upcomingBorder = medColor ? medColor.border : (theme === 'dark' ? 'border-slate-600' : 'border-blue-200');
                      const nextDoseTime = upcomingDoses.length > 0 ? formatTimeToReadable(upcomingDoses[0].scheduled_time) : '';

                return (
                        <div key={med.id} className={`flex items-center justify-between rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 lg:p-6 ${upcomingBg} border-2 ${upcomingBorder}`}>
                          <div className="flex items-center flex-1 min-w-0">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mr-2 sm:mr-3 md:mr-4 lg:mr-6 bg-blue-500 flex-shrink-0`}>
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} truncate`}>{med.name}</p>
                              <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold ${textColor} truncate`}>{med.instructions || `Take ${med.dosage}`}</p>
                              <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl ${textSecondary} truncate`}>
                                {upcomingDoses.length === med.doses.length
                                  ? 'All doses upcoming today'
                                  : `${upcomingDoses.length} of ${med.doses.length} doses upcoming`}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2 sm:ml-3">
                            <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} text-right`}>
                              {nextDoseTime}
                            </p>
                          </div>
                  </div>
                );
              })}
            </div>
                ) : (
                  <div className={`${theme === 'dark' ? 'bg-slate-700 bg-opacity-50' : 'bg-slate-100'} rounded-lg sm:rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8`}>
                    <p className={`text-sm sm:text-base md:text-lg lg:text-xl ${textSecondary} text-center`}>None upcoming</p>
                  </div>
                )}
              </div>

              {/* Missed Medications Section */}
              {missedMedications.length > 0 && (
                <div className="mb-4 sm:mb-6 md:mb-8">
                  <h3 className={`text-base sm:text-lg md:text-xl lg:text-2xl font-semibold ${textColor} mb-2 sm:mb-3 md:mb-4 flex items-center`}>
                    <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'} mr-1 sm:mr-2`} />
                    Missed ({missedMedications.length})
                  </h3>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {missedMedications.map(({ med, doseStatuses }) => {
                    const missedDoses = doseStatuses.filter(s => s.isMissed);
                    
                    // Safety check: don't render if no missed doses
                    if (missedDoses.length === 0) return null;
                    const medColor = getMedicationColor(med.id, medications);
                    const missedBg = medColor
                      ? (theme === 'dark' ? medColor.dark : medColor.light)
                      : (theme === 'dark' ? 'bg-red-600 bg-opacity-80' : 'bg-gradient-to-r from-red-50 to-pink-50');
                    const missedBorder = medColor ? medColor.border : (theme === 'dark' ? 'border-slate-600' : 'border-red-200');
                    const nextDoseTime = missedDoses.length > 0 ? formatTimeToReadable(missedDoses[0].scheduled_time) : '';

                    return (
                      <div key={med.id} className={`flex items-center justify-between rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 lg:p-6 ${missedBg} border-2 ${missedBorder}`}>
                        <div className="flex items-center flex-1 min-w-0">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mr-2 sm:mr-3 md:mr-4 lg:mr-6 bg-red-500 flex-shrink-0`}>
                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-white" />
          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} truncate`}>{med.name}</p>
                            <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold ${textColor} truncate`}>{med.instructions || `Take ${med.dosage}`}</p>
                            <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl ${textSecondary} truncate`}>
                              {missedDoses.length === med.doses.length
                                ? 'All doses missed today'
                                : `${missedDoses.length} of ${med.doses.length} doses missed`}
                            </p>
        </div>
            </div>
                        <div className="flex-shrink-0 ml-2 sm:ml-3">
                          <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} text-right`}>
                            {nextDoseTime}
                          </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
            )}

              {/* Taken Medications Section */}
              {takenMedications.length > 0 && (
                <div className="mb-4 sm:mb-6 md:mb-8">
                  <h3 className={`text-base sm:text-lg md:text-xl lg:text-2xl font-semibold ${textColor} mb-2 sm:mb-3 md:mb-4 flex items-center`}>
                    <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'} mr-1 sm:mr-2`} />
                    Taken ({takenMedications.length})
                  </h3>
                  <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {takenMedications.map(({ med, doseStatuses }) => {
                    const takenDoses = doseStatuses.filter(s => s.isTaken);
                    const medColor = getMedicationColor(med.id, medications);
                    const completedBg = medColor
                      ? (theme === 'dark' ? medColor.dark : medColor.light)
                      : (theme === 'dark' ? 'bg-emerald-600 bg-opacity-80' : 'bg-gradient-to-r from-emerald-50 to-teal-50');
                    const completedBorder = medColor ? medColor.border : (theme === 'dark' ? 'border-slate-600' : 'border-emerald-200');
                    const nextDoseTime = takenDoses.length > 0 ? formatTimeToReadable(takenDoses[0].scheduled_time) : '';

                    return (
                      <div key={med.id} className={`flex items-center justify-between rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 lg:p-6 ${completedBg} border-2 ${completedBorder}`}>
                        <div className="flex items-center flex-1 min-w-0">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mr-2 sm:mr-3 md:mr-4 lg:mr-6 bg-emerald-500 flex-shrink-0`}>
                            <Check className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-white" />
          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} truncate`}>{med.name}</p>
                            <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold ${textColor} truncate`}>{med.instructions || `Take ${med.dosage}`}</p>
                            <p className={`text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl ${textSecondary} truncate`}>
                              {takenDoses.length === med.doses.length
                                ? 'All doses taken today'
                                : `${takenDoses.length} of ${med.doses.length} doses taken`}
                            </p>
        </div>
      </div>
                        <div className="flex-shrink-0 ml-2 sm:ml-3">
                          <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold ${textColor} text-right`}>
                            {nextDoseTime}
                          </p>
      </div>
    </div>
  );
                  })}
        </div>
      </div>
            )}

              {/* Empty State - Only show if no taken or missed medications (upcoming section is always visible) */}
              {takenMedications.length === 0 && missedMedications.length === 0 && (
                <div className="text-center py-4 sm:py-6 md:py-8">
                  <p className={`text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl ${textSecondary}`}>No medications scheduled for today</p>
          </div>
              )}
          </div>
          </div>
        </div>
      </div>

      <PrimaryBottomNav 
        onNavigate={onNavigate} 
        theme={theme} 
        currentScreen="home"
        isListening={isListening}
        startListening={startListening}
        stopListening={stopListening}
        userName={userName}
        debugTimeOverride={debugTimeOverride}
        debugTime={debugTime}
      />
  </div>
  );
};


export const PrimaryCantTakeMedication = ({ onNavigate, speak, triggerSync, theme = 'light' }) => {
  const { bgClass, cardBg, textColor, borderColor } = useThemeStyles(theme);
  
  return (
  <div className={`min-h-screen ${bgClass} flex items-center justify-center p-12 relative`}>
    <div className="max-w-4xl w-full relative z-10">
      <div className={`${cardBg} rounded-3xl p-12 ${borderColor}`}>
        <h1 className={`text-5xl font-bold ${textColor} mb-4 text-center`}>Why can't you take it right now?</h1>
        <p className={`text-2xl ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'} mb-12 text-center`}>Select the reason that fits best</p>
        
        <div className="space-y-4">
          <button onClick={() => onNavigate('cant-find')} className={`w-full ${theme === 'dark' ? 'bg-blue-900 bg-opacity-60 hover:bg-blue-800 border-slate-600' : 'bg-blue-50 hover:bg-blue-100 border-2 border-blue-200'} rounded-2xl p-8 transition-all hover:scale-105 text-left ${borderColor}`}>
            <div className="flex items-center">
              <Search className={`w-12 h-12 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mr-6`}/>
              <div>
                <p className={`text-3xl font-bold ${textColor} mb-2`}>I CAN'T FIND THE PILL</p>
                <p className={`text-xl ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>→ I'll help you locate it</p>
              </div>
            </div>
          </button>

          <button onClick={() => {speak('Thank you for letting me know.'); onNavigate('home');}} className={`w-full ${theme === 'dark' ? 'bg-green-900 bg-opacity-60 hover:bg-green-800 border-slate-600' : 'bg-green-50 hover:bg-green-100 border-2 border-green-200'} rounded-2xl p-8 transition-all hover:scale-105 text-left ${borderColor}`}>
            <div className="flex items-center">
              <CheckCircle className={`w-12 h-12 ${theme === 'dark' ? 'text-green-300' : 'text-green-600'} mr-6`}/>
              <div>
                <p className={`text-3xl font-bold ${textColor} mb-2`}>I ALREADY TOOK IT EARLIER</p>
                <p className={`text-xl ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>→ Let me confirm the time</p>
              </div>
            </div>
          </button>

          <button onClick={() => {speak('I am sorry you are not feeling well.'); onNavigate('home'); triggerSync('Reported symptoms');}} className={`w-full ${theme === 'dark' ? 'bg-orange-900 bg-opacity-60 hover:bg-orange-800 border-slate-600' : 'bg-orange-50 hover:bg-orange-100 border-2 border-orange-200'} rounded-2xl p-8 transition-all hover:scale-105 text-left ${borderColor}`}>
            <div className="flex items-center">
              <AlertCircle className={`w-12 h-12 ${theme === 'dark' ? 'text-orange-300' : 'text-orange-600'} mr-6`}/>
              <div>
                <p className={`text-3xl font-bold ${textColor} mb-2`}>I'M NOT FEELING WELL</p>
                <p className={`text-xl ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>→ Tell me what's wrong</p>
              </div>
            </div>
          </button>

          <button onClick={() => {speak('I will help you order a refill.'); onNavigate('home');}} className={`w-full ${theme === 'dark' ? 'bg-purple-900 bg-opacity-60 hover:bg-purple-800 border-slate-600' : 'bg-purple-50 hover:bg-purple-100 border-2 border-purple-200'} rounded-2xl p-8 transition-all hover:scale-105 text-left ${borderColor}`}>
            <div className="flex items-center">
              <Pill className={`w-12 h-12 ${theme === 'dark' ? 'text-purple-300' : 'text-purple-600'} mr-6`}/>
              <div>
                <p className={`text-3xl font-bold ${textColor} mb-2`}>I'M OUT OF THIS MEDICATION</p>
                <p className={`text-xl ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>→ Let's order a refill</p>
              </div>
            </div>
          </button>
        </div>

        <button onClick={() => onNavigate('home')} className={`w-full mt-8 ${theme === 'dark' ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} text-2xl font-semibold py-4 rounded-xl ${borderColor}`}>
          <ArrowLeft className="w-8 h-8 inline mr-3"/>Go Back
        </button>
      </div>
    </div>
  </div>
  );
};

export const PrimaryCantFindMedication = ({ medications, currentMedIndex, onNavigate, speak, triggerSync, theme = 'light' }) => {
  const { bgClass, cardBg, textColor, borderColor } = useThemeStyles(theme);
  
  return (
  <div className={`min-h-screen ${bgClass} flex items-center justify-center p-12 relative`}>
    <div className="max-w-4xl w-full relative z-10">
      <div className={`${cardBg} rounded-3xl p-12 ${borderColor}`}>
        <h1 className={`text-5xl font-bold ${textColor} mb-8 text-center`}>Let me help you find it</h1>
        
        <div className={`${theme === 'dark' ? 'bg-blue-900 bg-opacity-60 border-slate-600' : 'bg-blue-50 border-2 border-blue-300'} rounded-3xl p-10 mb-8 ${borderColor}`}>
          <div className="flex items-start mb-6">
            <Search className={`w-16 h-16 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mr-6 flex-shrink-0`}/>
            <div>
              <p className={`text-3xl font-bold ${textColor} mb-4`}>Looking for:</p>
              <p className={`text-4xl font-bold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mb-2`}>{medications[currentMedIndex].name}</p>
              <p className={`text-3xl font-semibold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mb-6`}>{medications[currentMedIndex].instructions || `Take ${medications[currentMedIndex].dosage}`}</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className={`${theme === 'dark' ? 'bg-slate-700 bg-opacity-60 border-slate-600' : 'bg-white border-2 border-blue-200'} rounded-2xl p-6 ${borderColor}`}>
              <p className={`text-2xl font-semibold ${textColor} mb-3`}>Step 1: Check your medication organizer</p>
              <p className={`text-xl ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>Look in the <strong>Monday morning</strong> compartment</p>
            </div>

            <div className={`${theme === 'dark' ? 'bg-slate-700 bg-opacity-60 border-slate-600' : 'bg-white border-2 border-blue-200'} rounded-2xl p-6 ${borderColor}`}>
              <p className={`text-2xl font-semibold ${textColor} mb-3`}>Step 2: Look for the bottle</p>
              <p className={`text-xl ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}><strong>{medications[currentMedIndex].location}</strong></p>
              <p className={`text-xl ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'} mt-2`}>It should be on your <strong>kitchen counter</strong></p>
            </div>

            <div className={`${theme === 'dark' ? 'bg-slate-700 bg-opacity-60 border-slate-600' : 'bg-white border-2 border-blue-200'} rounded-2xl p-6 ${borderColor}`}>
              <p className={`text-2xl font-semibold ${textColor} mb-3`}>What it looks like:</p>
              <p className={`text-xl ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>{medications[currentMedIndex].form}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <button onClick={() => onNavigate('home')} className={`${buttonStyles.primary} ${buttonStyles.medium} hover:scale-105 transition-all ${borderColor}`}>
            <Check className={`w-12 h-12 mx-auto mb-3 ${'text-white'}`}/>Found It!
          </button>
          <button onClick={() => {speak('I am calling Sarah for you.'); triggerSync('Called for help'); onNavigate('home');}} className={`${buttonStyles.accent} ${buttonStyles.medium} hover:scale-105 transition-all ${borderColor}`}>
            <Phone className={`w-12 h-12 mx-auto mb-3 ${'text-white'}`}/>Call Sarah
          </button>
        </div>

        <button onClick={() => onNavigate('cant-take')} className={`w-full mt-6 ${theme === 'dark' ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} text-2xl font-semibold py-4 rounded-xl ${borderColor}`}>
          <ArrowLeft className="w-8 h-8 inline mr-3"/>Go Back
        </button>
      </div>
    </div>
  </div>
  );
};

export const PrimaryMedicationList = ({ userName, medications, medicationsTaken, onNavigate, theme = 'light', medicationLogs = [], debugTimeOverride = false, debugTime = null }) => {
  const { bgClass, cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);
  
  // Get current time (debug or real)
  const currentTime = debugTimeOverride && debugTime ? debugTime : new Date();


  // Function to get dose status icon and color
  const getDoseStatusInfo = (isTaken, isMissed, isUpcoming, isDue, isLate) => {
    if (isTaken) {
      return {
        icon: Check,
        color: theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600',
        bgColor: theme === 'dark' ? 'bg-emerald-600 bg-opacity-80' : 'bg-emerald-50',
        borderColor: theme === 'dark' ? 'border-emerald-500' : 'border-emerald-300',
        status: 'Taken'
      };
    } else if (isMissed) {
      return {
        icon: AlertTriangle,
        color: theme === 'dark' ? 'text-red-400' : 'text-red-600',
        bgColor: theme === 'dark' ? 'bg-red-600 bg-opacity-80' : 'bg-red-50',
        borderColor: theme === 'dark' ? 'border-red-500' : 'border-red-300',
        status: 'Missed'
      };
    } else if (isUpcoming) {
      return {
        icon: Clock,
        color: theme === 'dark' ? 'text-orange-400' : 'text-orange-600',
        bgColor: theme === 'dark' ? 'bg-orange-600 bg-opacity-80' : 'bg-orange-50',
        borderColor: theme === 'dark' ? 'border-orange-500' : 'border-orange-300',
        status: 'Upcoming'
      };
    } else if (isDue || isLate) {
      // Show due/late doses as "Due" with a blue color
      return {
        icon: Clock,
        color: theme === 'dark' ? 'text-blue-400' : 'text-blue-600',
        bgColor: theme === 'dark' ? 'bg-blue-600 bg-opacity-80' : 'bg-blue-50',
        borderColor: theme === 'dark' ? 'border-blue-500' : 'border-blue-300',
        status: isDue ? 'Due' : 'Late'
      };
    }
    // Fallback: show all doses, even if status is unclear
    return {
      icon: Clock,
      color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600',
      bgColor: theme === 'dark' ? 'bg-gray-600 bg-opacity-80' : 'bg-gray-50',
      borderColor: theme === 'dark' ? 'border-gray-500' : 'border-gray-300',
      status: 'Scheduled'
    };
  };
  
  return (
  <div className={`min-h-screen ${bgClass} flex flex-col relative pb-24`}>
    <div className="flex-1 p-4 sm:p-6 md:p-8 lg:p-12 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
      {medications.map((med, index) => {
          const doseStatuses = med.doses && med.doses.length > 0 ? getDoseStatus(med, medicationLogs, currentTime) : [];
          const takenDoses = doseStatuses.filter(s => s.isTaken);
          const missedDoses = doseStatuses.filter(s => s.isMissed);
          const lateDoses = doseStatuses.filter(s => s.isLate);
          const dueDoses = doseStatuses.filter(s => s.isDue);
          const upcomingDoses = doseStatuses.filter(s => s.isUpcoming);

          const medicationColor = getMedicationColor(index, medications);
          const cardBgColor = theme === 'dark' ? medicationColor.dark : medicationColor.light;
          const cardBorderColor = medicationColor.border;

        return (
          <div key={index} className={`${cardBgColor} ${cardBorderColor} border-2 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 flex flex-col`}>
            {/* Medication Header */}
            <div className="text-center mb-4 sm:mb-6">
              <p className={`text-xl sm:text-2xl md:text-3xl font-bold ${textColor} mb-1 sm:mb-2 break-words`}>{med.name}</p>
              <p className={`text-lg sm:text-xl md:text-2xl font-semibold ${textColor} mb-2 sm:mb-3`}>{med.instructions || `Take ${med.dosage}`}</p>
              <p className={`text-sm sm:text-base md:text-lg ${textSecondary} mb-3`}>For: {med.purpose}</p>
            </div>

            {/* Individual Dose Details */}
            {doseStatuses.length > 0 && (
              <div className="flex-1">
                <p className={`text-sm sm:text-base md:text-lg font-semibold ${textColor} mb-2 sm:mb-3`}>Dose Schedule:</p>
                <div className="space-y-2 sm:space-y-3">
                  {doseStatuses.map((doseStatus, doseIndex) => {
                    const statusInfo = getDoseStatusInfo(
                      doseStatus.isTaken, 
                      doseStatus.isMissed, 
                      doseStatus.isUpcoming,
                      doseStatus.isDue,
                      doseStatus.isLate
                    );

                    const StatusIcon = statusInfo.icon;
                    return (
                      <div key={doseIndex} className={`flex items-center justify-between p-2 sm:p-3 rounded-lg ${statusInfo.bgColor} border ${statusInfo.borderColor}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center mb-1">
                            <StatusIcon className={`w-3 h-3 sm:w-4 sm:h-4 ${statusInfo.color} mr-2 flex-shrink-0`} />
                            <span className={`text-xs sm:text-sm font-medium ${textColor}`}>Dose {doseStatus.dose_number}</span>
                          </div>
                          <p className={`text-xs sm:text-sm ${statusInfo.color} truncate`}>{doseStatus.guidanceMessage}</p>
                        </div>
                        <span className={`text-xs sm:text-sm font-bold ${textColor} ml-2 flex-shrink-0`}>
                          {formatTimeToReadable(doseStatus.scheduled_time)}
                        </span>
                      </div>
                    );
                  })}
              </div>
              </div>
            )}

            {/* No doses message */}
            {(!doseStatuses || doseStatuses.length === 0) && (
              <div className="flex-1 flex items-center justify-center">
                <p className={`text-sm sm:text-base md:text-lg ${textSecondary} text-center`}>
                  No scheduled doses today
                </p>
            </div>
            )}
          </div>
        )})}
        </div>
    </div>

    <PrimaryBottomNav
      onNavigate={onNavigate}
      theme={theme}
      currentScreen="medication-list"
      userName={userName}
      debugTimeOverride={debugTimeOverride}
      debugTime={debugTime}
    />
  </div>
  );
};

export const PrimaryHelp = ({ userName, onNavigate, theme = 'light', debugTimeOverride = false, debugTime = null }) => {
  const { bgClass, cardBg, textColor, textSecondary, borderColor } = useThemeStyles(theme);
  const contactName = 'Sarah';
  const contactNumber = '(555) 123-4567';

  const handleCallDoctor = () => {
    // In a real app, this would initiate a phone call
    console.log('Calling doctor office now');
    // Example: window.location.href = 'tel:+15551234567';
  };

  const handleScheduleVisit = () => {
    // In a real app, this would open a scheduling interface
    console.log('Scheduling visit');
    // Example: navigate to scheduling screen or open calendar
  };

  const handleCall = () => {
    console.log('Calling', contactName);
    // Example: window.location.href = 'tel:+15551234567';
  };

  const handleMessage = () => {
    console.log('Messaging', contactName);
  };

  const handleVideoCall = () => {
    console.log('Video calling', contactName);
  };

  return (
    <div className={`h-screen ${bgClass} flex flex-col relative overflow-hidden`}>
      <div className="flex-1 p-4 sm:p-6 md:p-8 relative z-10 overflow-y-auto flex items-center justify-center">
        <div className="max-w-6xl w-full flex flex-col -mt-16 sm:-mt-20 md:-mt-24">
          {/* Support Person Contact Section - First */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold ${textColor} mb-3 sm:mb-4 md:mb-6 text-center`}>
              Contact Support
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {/* Call Box */}
              <button 
                onClick={handleCall}
                className={`bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 transition-all hover:scale-105 hover:brightness-110 min-h-[140px] sm:min-h-[160px] md:min-h-[180px] flex flex-col items-center justify-center ${borderColor} border-2`}
                aria-label={`Call ${contactName} at ${contactNumber}`}
              >
                <Phone className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 text-white" aria-hidden="true"/>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white">Call {contactName}</p>
                <p className="text-sm sm:text-base md:text-lg opacity-90 text-white">{contactNumber}</p>
              </button>

              {/* Message Box */}
              <button 
                onClick={handleMessage}
                className={`bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 transition-all hover:scale-105 hover:brightness-110 min-h-[140px] sm:min-h-[160px] md:min-h-[180px] flex flex-col items-center justify-center ${borderColor} border-2`}
                aria-label={`Message ${contactName} at ${contactNumber}`}
              >
                <MessageSquare className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 text-white" aria-hidden="true"/>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white">Message {contactName}</p>
                <p className="text-sm sm:text-base md:text-lg opacity-90 text-white">{contactNumber}</p>
              </button>

              {/* Video Call Box */}
              <button 
                onClick={handleVideoCall}
                className={`bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-500 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 transition-all hover:scale-105 hover:brightness-110 min-h-[140px] sm:min-h-[160px] md:min-h-[180px] flex flex-col items-center justify-center ${borderColor} border-2`}
                aria-label={`Video call ${contactName} at ${contactNumber}`}
              >
                <Video className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-2 sm:mb-3 text-white" aria-hidden="true"/>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white">Video Call {contactName}</p>
                <p className="text-sm sm:text-base md:text-lg opacity-90 text-white">{contactNumber}</p>
              </button>
            </div>
          </div>

          {/* Physician Contact Section - Second */}
          <div>
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold ${textColor} mb-3 sm:mb-4 md:mb-6 text-center`}>
              Contact Your Physician
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              {/* Call Doctor Button */}
              <button 
                onClick={handleCallDoctor}
                className={`bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 transition-all hover:scale-105 hover:brightness-110 min-h-[140px] sm:min-h-[160px] md:min-h-[180px] flex flex-col items-center justify-center ${borderColor} border-2`}
                aria-label="Call doctor office now for urgent matters"
              >
                <Phone className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-2 sm:mb-3 text-white" aria-hidden="true"/>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white">Call Doctor</p>
                <p className="text-sm sm:text-base md:text-lg opacity-90 text-white">Call office now</p>
              </button>

              {/* Schedule Visit Button */}
              <button 
                onClick={handleScheduleVisit}
                className={`bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 transition-all hover:scale-105 hover:brightness-110 min-h-[140px] sm:min-h-[160px] md:min-h-[180px] flex flex-col items-center justify-center ${borderColor} border-2`}
                aria-label="Schedule your next appointment with your physician"
              >
                <CalendarDays className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-2 sm:mb-3 text-white" aria-hidden="true"/>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white">Schedule Visit</p>
                <p className="text-sm sm:text-base md:text-lg opacity-90 text-white">Plan your next appointment</p>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <PrimaryBottomNav 
        onNavigate={onNavigate} 
        theme={theme} 
        currentScreen="help"
        userName={userName}
        debugTimeOverride={debugTimeOverride}
        debugTime={debugTime}
      />
    </div>
  );
};

export const PrimarySuccess = ({ medications, medicationsTaken, onNavigate, onReset, theme = 'light', medicationLogs = [], debugTimeOverride = false, debugTime = null }) => {
  const { cardBg, textColor, textSecondary } = useThemeStyles(theme);
  const borderColor = theme === 'dark' ? 'border-slate-600 border-opacity-50 border-2' : 'border-emerald-200 border-2';
  
  // Get current time (debug or real)
  const currentTime = debugTimeOverride && debugTime ? debugTime : new Date();
  
  return (
  <div className={`min-h-screen ${theme === 'light' ? 'bg-gradient-to-br from-emerald-50 to-teal-50' : 'bg-gradient-to-br from-emerald-900 via-teal-900 to-green-900'} flex items-center justify-center p-12 relative`}>
    <div className="max-w-2xl w-full text-center relative z-10">
      <div className={`w-32 h-32 ${'bg-gradient-to-br from-emerald-500 to-teal-500'} rounded-full mx-auto flex items-center justify-center mb-8`}>
        <Check className="w-16 h-16 text-white"/>
      </div>
      <h1 className={`text-6xl font-bold ${textColor} mb-4`}>Excellent Work!</h1>
      <p className={`text-3xl ${textSecondary} mb-8`}>
        {(() => {
          let totalDosesTaken = 0;
          let totalDoses = 0;
          medications.forEach(med => {
            if (med.doses && med.doses.length > 0) {
              const doseStatuses = getDoseStatus(med, medicationLogs, currentTime);
              totalDoses += doseStatuses.length;
              totalDosesTaken += doseStatuses.filter(s => s.isTaken).length;
            }
          });
          
          if (totalDosesTaken === totalDoses && totalDoses > 0) {
            return "You've completed all your doses for today";
          } else if (totalDosesTaken === 1) {
            return "You've completed 1 dose";
          } else if (totalDosesTaken > 0) {
            return `You've completed ${totalDosesTaken} dose${totalDosesTaken > 1 ? 's' : ''} today`;
          } else {
            return "Great job!";
          }
        })()}
      </p>
      <div className={`${cardBg} rounded-3xl p-8 mb-8 ${borderColor}`}>
        <p className={`text-2xl font-bold ${textColor} mb-6`}>DOSES TAKEN:</p>
        <div className="space-y-4">
          {medications.map((m) => {
            if (!m.doses || m.doses.length === 0) return null;
            const doseStatuses = getDoseStatus(m, medicationLogs, currentTime);
            const takenDoses = doseStatuses.filter(s => s.isTaken);
            if (takenDoses.length === 0) return null;
            
            return takenDoses.map((doseStatus, doseIndex) => (
              <div key={`${m.id}-${doseStatus.dose_number}`} className={`flex items-center justify-between p-4 ${theme === 'dark' ? 'bg-emerald-600 bg-opacity-60 border-slate-600' : 'bg-emerald-50 border-2 border-emerald-200'} rounded-xl ${borderColor}`}>
              <div className="flex items-center">
                <Check className={`w-8 h-8 ${theme === 'dark' ? 'text-emerald-200' : 'text-emerald-600'} mr-4`}/>
                  <div>
                    <p className={`text-2xl font-semibold ${textColor} mb-1`}>{m.name}</p>
                    <p className={`text-xl font-semibold ${textColor}`}>{m.instructions || `Take ${m.dosage}`}</p>
                    {doseStatuses.length > 1 && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-emerald-200' : 'text-emerald-600'}`}>
                        Dose {doseStatus.dose_number} of {doseStatuses.length}
                      </p>
                    )}
                  </div>
              </div>
              <span className={`text-lg ${theme === 'dark' ? 'text-slate-300' : 'text-slate-500'}`}>
                  {formatTimeToReadable(doseStatus.scheduled_time)}
              </span>
            </div>
            ));
          }).flat().filter(Boolean)}
        </div>
      </div>
      <button onClick={() => {onNavigate('home'); onReset();}} className={`w-full ${'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white'} text-2xl font-semibold py-6 rounded-xl ${borderColor}`}>
        <Home className={`w-8 h-8 inline mr-3 ${'text-white'}`}/>RETURN HOME
      </button>
    </div>
  </div>
  );
};


